import { useMemo } from 'react';
import { usePartnerRequests, type PartnerRequest } from './usePartnerRequests';
import { useWelcomeInOptIns, type WelcomeInOptInUser } from './useWelcomeInOptIns';
import { normalizeEmail, normalizePhone } from '../utils/contactMatching';

export type WelcomeInPartnerConversion = {
  welcomeInId: string;
  partnerRequestId: string;
  userId: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  welcomeInDate: string | null;
  partnerDate: string | null;
};

export type WelcomeInPartnerConversionSummary = {
  welcomeInTotal: number;
  partnerTotal: number;
  convertedTotal: number;
  matchedPartnerTotal: number;
  optInConversionPercent: number;
  partnerOverlapPercent: number;
};

function percentOf(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  return Math.round((numerator / denominator) * 100);
}

function splitPartnerName(name?: string | null): { first: string; last: string } {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (!parts.length) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

function pickEarlierPartner(
  current: PartnerRequest | undefined,
  candidate: PartnerRequest
): PartnerRequest {
  if (!current) return candidate;
  const currentDate = current.created_at ? new Date(current.created_at).getTime() : Infinity;
  const candidateDate = candidate.created_at ? new Date(candidate.created_at).getTime() : Infinity;
  return candidateDate < currentDate ? candidate : current;
}

function buildPartnerLookups(partnerRequests: PartnerRequest[]) {
  const byEmail = new Map<string, PartnerRequest>();
  const byPhone = new Map<string, PartnerRequest>();

  for (const request of partnerRequests) {
    const email = normalizeEmail(request.email);
    const phone = normalizePhone(request.phone);

    if (email) {
      byEmail.set(email, pickEarlierPartner(byEmail.get(email), request));
    }
    if (phone) {
      byPhone.set(phone, pickEarlierPartner(byPhone.get(phone), request));
    }
  }

  return { byEmail, byPhone };
}

function findMatchingPartner(
  optIn: WelcomeInOptInUser,
  lookups: ReturnType<typeof buildPartnerLookups>
): PartnerRequest | null {
  const email = normalizeEmail(optIn.email);
  const phone = normalizePhone(optIn.phone);

  const byEmail = email ? lookups.byEmail.get(email) : undefined;
  const byPhone = phone ? lookups.byPhone.get(phone) : undefined;

  if (byEmail && byPhone && byEmail.id !== byPhone.id) {
    return pickEarlierPartner(byEmail, byPhone);
  }

  return byEmail ?? byPhone ?? null;
}

function toConversionRow(
  optIn: WelcomeInOptInUser,
  partner: PartnerRequest
): WelcomeInPartnerConversion {
  const partnerName = splitPartnerName(partner.name);
  const firstName = optIn.first_name?.trim() || partnerName.first || '-';
  const lastName = optIn.last_name?.trim() || partnerName.last || '-';

  return {
    welcomeInId: optIn.id,
    partnerRequestId: partner.id,
    userId: partner.user_id ?? null,
    firstName,
    lastName,
    phone: optIn.phone ?? partner.phone ?? null,
    email: optIn.email ?? partner.email ?? null,
    welcomeInDate: optIn.opted_in_at ?? optIn.created_at ?? null,
    partnerDate: partner.created_at ?? null,
  };
}

export const useWelcomeInPartnerConversions = () => {
  const {
    data: welcomeInOptIns = [],
    isLoading: isWelcomeInLoading,
    error: welcomeInError,
  } = useWelcomeInOptIns();
  const {
    data: partnerRequests = [],
    isLoading: isPartnerLoading,
    error: partnerError,
  } = usePartnerRequests();

  const rows = useMemo(() => {
    const lookups = buildPartnerLookups(partnerRequests);
    const matched: WelcomeInPartnerConversion[] = [];

    for (const optIn of welcomeInOptIns) {
      const partner = findMatchingPartner(optIn, lookups);
      if (!partner) continue;
      matched.push(toConversionRow(optIn, partner));
    }

    return matched.sort((a, b) => {
      const aTime = a.welcomeInDate ? new Date(a.welcomeInDate).getTime() : 0;
      const bTime = b.welcomeInDate ? new Date(b.welcomeInDate).getTime() : 0;
      return bTime - aTime;
    });
  }, [welcomeInOptIns, partnerRequests]);

  const summary = useMemo<WelcomeInPartnerConversionSummary>(() => {
    const matchedPartnerTotal = new Set(rows.map((row) => row.partnerRequestId)).size;
    const welcomeInTotal = welcomeInOptIns.length;
    const partnerTotal = partnerRequests.length;
    return {
      welcomeInTotal,
      partnerTotal,
      convertedTotal: rows.length,
      matchedPartnerTotal,
      optInConversionPercent: percentOf(rows.length, welcomeInTotal),
      partnerOverlapPercent: percentOf(matchedPartnerTotal, partnerTotal),
    };
  }, [welcomeInOptIns.length, partnerRequests.length, rows]);

  return {
    rows,
    summary,
    isLoading: isWelcomeInLoading || isPartnerLoading,
    error: welcomeInError ?? partnerError ?? null,
  };
};
